import React, { useState, useRef, useEffect, useCallback } from 'react';

// Parchment-style Westeros map background — public domain style, safe to hotlink.
// Can be swapped by editing this constant.
const WESTEROS_IMAGE = 'https://static.wikia.nocookie.net/gameofthrones/images/0/04/MapOfWesteros.jpg';

// Major Westeros + Essos locations (positioned as % of the map image, 0-100).
// The map is taller than wide; Westeros occupies the left 55%, Essos the right.
const LOCATIONS = [
  // ═══ The North ═══
  { id: 'the_wall', name: 'The Wall', x: 36, y: 8, region: 'The North', major: true },
  { id: 'castle_black', name: 'Castle Black', x: 36, y: 10, region: 'The Wall' },
  { id: 'winterfell', name: 'Winterfell', x: 35, y: 25, region: 'The North', major: true },
  { id: 'white_harbor', name: 'White Harbor', x: 40, y: 31, region: 'The North' },
  { id: 'the_dreadfort', name: 'The Dreadfort', x: 39, y: 23, region: 'The North' },
  { id: 'the_twins', name: 'The Twins', x: 35, y: 35, region: 'The Riverlands', major: true },
  { id: 'bear_island', name: 'Bear Island', x: 28, y: 22, region: 'The North' },
  { id: 'last_hearth', name: 'Last Hearth', x: 38, y: 16, region: 'The North' },
  { id: 'karhold', name: 'Karhold', x: 42, y: 18, region: 'The North' },

  // ═══ The Riverlands ═══
  { id: 'riverrun', name: 'Riverrun', x: 33, y: 40, region: 'The Riverlands', major: true },
  { id: 'harrenhal', name: 'Harrenhal', x: 36, y: 42, region: 'The Riverlands' },
  { id: 'stoney_sept', name: 'Stoney Sept', x: 34, y: 45, region: 'The Riverlands' },
  { id: 'trident', name: 'The Trident', x: 37, y: 38, region: 'The Riverlands' },

  // ═══ The Vale ═══
  { id: 'the_eyrie', name: 'The Eyrie', x: 42, y: 40, region: 'The Vale', major: true },
  { id: 'gulltown', name: 'Gulltown', x: 46, y: 42, region: 'The Vale' },
  { id: 'runestone', name: 'Runestone', x: 45, y: 39, region: 'The Vale' },

  // ═══ The Westerlands ═══
  { id: 'casterly_rock', name: 'Casterly Rock', x: 28, y: 46, region: 'The Westerlands', major: true },
  { id: 'lannisport', name: 'Lannisport', x: 28, y: 48, region: 'The Westerlands' },
  { id: 'golden_tooth', name: 'Golden Tooth', x: 30, y: 43, region: 'The Westerlands' },

  // ═══ The Iron Islands ═══
  { id: 'pyke', name: 'Pyke', x: 26, y: 37, region: 'Iron Islands', major: true },

  // ═══ The Crownlands ═══
  { id: 'kings_landing', name: "King's Landing", x: 40, y: 50, region: 'The Crownlands', major: true },
  { id: 'dragonstone', name: 'Dragonstone', x: 44, y: 50, region: 'The Crownlands', major: true },
  { id: 'rosby', name: 'Rosby', x: 41, y: 48, region: 'The Crownlands' },

  // ═══ The Stormlands ═══
  { id: 'storms_end', name: "Storm's End", x: 42, y: 58, region: 'The Stormlands', major: true },
  { id: 'nightsong', name: 'Nightsong', x: 39, y: 60, region: 'The Stormlands' },

  // ═══ The Reach ═══
  { id: 'highgarden', name: 'Highgarden', x: 33, y: 58, region: 'The Reach', major: true },
  { id: 'oldtown', name: 'Oldtown', x: 30, y: 64, region: 'The Reach', major: true },
  { id: 'the_arbor', name: 'The Arbor', x: 29, y: 68, region: 'The Reach' },
  { id: 'horn_hill', name: 'Horn Hill', x: 33, y: 61, region: 'The Reach' },

  // ═══ Dorne ═══
  { id: 'sunspear', name: 'Sunspear', x: 43, y: 68, region: 'Dorne', major: true },
  { id: 'starfall', name: 'Starfall', x: 33, y: 68, region: 'Dorne' },
  { id: 'yronwood', name: 'Yronwood', x: 39, y: 66, region: 'Dorne' },
  { id: 'tower_of_joy', name: 'Tower of Joy', x: 35, y: 69, region: 'Dorne' },

  // ═══ Essos ═══
  { id: 'pentos', name: 'Pentos', x: 58, y: 46, region: 'Essos', major: true },
  { id: 'braavos', name: 'Braavos', x: 56, y: 35, region: 'Essos', major: true },
  { id: 'lorath', name: 'Lorath', x: 59, y: 38, region: 'Essos' },
  { id: 'norvos', name: 'Norvos', x: 62, y: 44, region: 'Essos' },
  { id: 'qohor', name: 'Qohor', x: 66, y: 44, region: 'Essos' },
  { id: 'myr', name: 'Myr', x: 60, y: 52, region: 'Essos' },
  { id: 'tyrosh', name: 'Tyrosh', x: 56, y: 54, region: 'Essos' },
  { id: 'lys', name: 'Lys', x: 54, y: 58, region: 'Essos' },
  { id: 'volantis', name: 'Volantis', x: 64, y: 58, region: 'Essos' },
  { id: 'vaes_dothrak', name: 'Vaes Dothrak', x: 74, y: 42, region: 'Essos', major: true },
  { id: 'meereen', name: 'Meereen', x: 78, y: 62, region: 'Slaver\'s Bay', major: true },
  { id: 'astapor', name: 'Astapor', x: 74, y: 66, region: 'Slaver\'s Bay' },
  { id: 'yunkai', name: 'Yunkai', x: 76, y: 64, region: 'Slaver\'s Bay' },
  { id: 'qarth', name: 'Qarth', x: 86, y: 70, region: 'Essos', major: true },
];

export { LOCATIONS };

// Parse "298 AC" / "283 BC" into signed year
function parseYear(position) {
  if (!position) return null;
  const p = String(position).trim().toUpperCase();
  try {
    if (p.includes('BC')) return -parseInt(p.replace('BC', '').trim(), 10);
    if (p.includes('AC')) return parseInt(p.replace('AC', '').trim(), 10);
    const n = parseInt(p, 10);
    return Number.isNaN(n) ? null : n;
  } catch (e) { return null; }
}

// Era-aware house territories — drives which borders render on the map.
// All coordinates are viewBox 0-100 (same as image). Westeros paths roughly match the continent outline.
function getHousesForYear(year) {
  if (year === null || year === undefined) year = 298;

  // Shared territories present in multiple eras
  const essos = { id: 'essos-free', label: 'FREE CITIES', color: '168,85,247', // purple
    path: 'M52,32 L62,30 L72,38 L70,56 L60,60 L52,58 L50,46 Z',
    textX: 60, textY: 44, fontSize: 1.6, dashed: true };
  const dothraki = { id: 'dothraki', label: 'DOTHRAKI SEA', color: '180,83,9', // burnt orange
    path: 'M68,32 L82,30 L86,44 L78,50 L68,48 Z',
    textX: 76, textY: 40, fontSize: 1.3, dashed: true };
  const slavers_bay = { id: 'slavers-bay', label: "SLAVER'S BAY", color: '161,98,7', // amber-700
    path: 'M70,58 L84,56 L88,68 L80,74 L72,70 Z',
    textX: 79, textY: 65, fontSize: 1.3, dashed: true };

  if (year < 281) {
    // ═══ Targaryen Dynasty — one crown rules all Seven Kingdoms ═══
    return [
      { id: 'targaryen-realm', label: 'HOUSE TARGARYEN', color: '220,38,38', // red / blood
        path: 'M26,8 L46,8 L48,30 L46,55 L44,72 L32,72 L26,60 L24,32 Z',
        textX: 35, textY: 40, fontSize: 1.8 },
      essos, dothraki, slavers_bay,
    ];
  }

  if (year >= 281 && year <= 283) {
    // ═══ Robert's Rebellion — open warfare ═══
    return [
      { id: 'loyalist', label: 'TARGARYEN LOYALISTS', color: '220,38,38',
        path: 'M38,46 L46,46 L46,54 L38,54 Z',
        textX: 42, textY: 44, fontSize: 1.2 },
      { id: 'rebels-stark', label: 'HOUSE STARK', color: '148,163,184', // slate-400
        path: 'M26,8 L44,8 L46,30 L26,30 Z',
        textX: 35, textY: 20, fontSize: 1.4 },
      { id: 'rebels-baratheon', label: 'HOUSE BARATHEON', color: '250,204,21', // yellow
        path: 'M38,55 L46,55 L44,65 L38,65 Z',
        textX: 42, textY: 60, fontSize: 1.2 },
      { id: 'rebels-tully', label: 'HOUSE TULLY', color: '37,99,235', // blue
        path: 'M30,36 L38,36 L38,46 L30,46 Z',
        textX: 34, textY: 41, fontSize: 1.2 },
      { id: 'lannister-neutral', label: 'HOUSE LANNISTER', color: '217,119,6', // amber / crimson
        path: 'M26,44 L32,44 L32,52 L26,52 Z',
        textX: 29, textY: 48, fontSize: 1.2, dashed: true },
      essos, dothraki, slavers_bay,
    ];
  }

  if (year >= 284 && year <= 297) {
    // ═══ Baratheon Peace — Robert rules; houses hold their regions ═══
    return [
      { id: 'baratheon-crown', label: 'HOUSE BARATHEON', color: '250,204,21', // yellow crown
        path: 'M38,46 L46,46 L46,62 L38,62 Z',
        textX: 42, textY: 54, fontSize: 1.4 },
      { id: 'stark', label: 'HOUSE STARK', color: '148,163,184',
        path: 'M26,8 L44,8 L46,30 L26,30 Z',
        textX: 35, textY: 20, fontSize: 1.4 },
      { id: 'arryn', label: 'HOUSE ARRYN', color: '96,165,250', // sky
        path: 'M40,32 L48,32 L48,44 L40,44 Z',
        textX: 44, textY: 38, fontSize: 1.2 },
      { id: 'tully', label: 'HOUSE TULLY', color: '37,99,235',
        path: 'M30,34 L40,34 L40,42 L30,42 Z',
        textX: 35, textY: 38, fontSize: 1.1 },
      { id: 'lannister', label: 'HOUSE LANNISTER', color: '220,38,38', // crimson
        path: 'M24,42 L32,42 L32,52 L24,52 Z',
        textX: 28, textY: 47, fontSize: 1.2 },
      { id: 'greyjoy', label: 'HOUSE GREYJOY', color: '71,85,105', // slate
        path: 'M22,34 L28,34 L28,40 L22,40 Z',
        textX: 25, textY: 37, fontSize: 1.0, dashed: true },
      { id: 'tyrell', label: 'HOUSE TYRELL', color: '22,163,74', // green
        path: 'M26,54 L38,54 L38,66 L26,66 Z',
        textX: 32, textY: 60, fontSize: 1.3 },
      { id: 'martell', label: 'HOUSE MARTELL', color: '234,88,12', // orange
        path: 'M30,66 L46,66 L46,72 L30,72 Z',
        textX: 38, textY: 70, fontSize: 1.3 },
      essos, dothraki, slavers_bay,
    ];
  }

  if (year >= 298 && year <= 300) {
    // ═══ War of Five Kings — realm fractured ═══
    return [
      { id: 'stark-king', label: 'KING IN THE NORTH', color: '148,163,184',
        path: 'M26,8 L46,8 L46,36 L26,36 Z',
        textX: 36, textY: 22, fontSize: 1.4 },
      { id: 'lannister-crown', label: 'HOUSE LANNISTER', color: '220,38,38',
        path: 'M22,38 L38,38 L40,52 L22,52 Z',
        textX: 30, textY: 45, fontSize: 1.4 },
      { id: 'joffrey-crown', label: 'IRON THRONE', color: '250,204,21',
        path: 'M38,46 L48,46 L48,55 L38,55 Z',
        textX: 43, textY: 51, fontSize: 1.2 },
      { id: 'stannis', label: 'STANNIS', color: '34,211,238', // cyan — R'hllor
        path: 'M43,48 L48,48 L48,53 L43,53 Z',
        textX: 46, textY: 50, fontSize: 0.9, dashed: true },
      { id: 'renly', label: 'RENLY', color: '22,163,74', // green — Reach ally
        path: 'M38,56 L46,56 L46,62 L38,62 Z',
        textX: 42, textY: 59, fontSize: 1.0, dashed: true },
      { id: 'greyjoy-king', label: 'HOUSE GREYJOY', color: '71,85,105',
        path: 'M22,34 L30,34 L30,42 L22,42 Z',
        textX: 26, textY: 37, fontSize: 1.1, dashed: true },
      { id: 'tyrell', label: 'HOUSE TYRELL', color: '22,163,74',
        path: 'M26,56 L36,56 L36,66 L26,66 Z',
        textX: 31, textY: 61, fontSize: 1.2 },
      { id: 'martell', label: 'HOUSE MARTELL', color: '234,88,12',
        path: 'M30,66 L46,66 L46,72 L30,72 Z',
        textX: 38, textY: 70, fontSize: 1.3 },
      essos, dothraki, slavers_bay,
    ];
  }

  if (year >= 301 && year <= 303) {
    // ═══ After Red Wedding — Bolton North, Lannister/Tyrell alliance ═══
    return [
      { id: 'bolton', label: 'HOUSE BOLTON', color: '136,19,55', // dark red
        path: 'M26,8 L46,8 L46,36 L26,36 Z',
        textX: 36, textY: 22, fontSize: 1.4, dashed: true },
      { id: 'lannister-tyrell', label: 'LANNISTER-TYRELL', color: '220,38,38',
        path: 'M22,38 L46,38 L46,66 L22,66 Z',
        textX: 34, textY: 52, fontSize: 1.4 },
      { id: 'targaryen-meereen', label: 'HOUSE TARGARYEN', color: '124,45,18', // ember
        path: 'M70,58 L84,56 L88,68 L80,74 L72,70 Z',
        textX: 79, textY: 65, fontSize: 1.3 },
      { id: 'martell', label: 'HOUSE MARTELL', color: '234,88,12',
        path: 'M30,66 L46,66 L46,72 L30,72 Z',
        textX: 38, textY: 70, fontSize: 1.3 },
      essos, dothraki,
    ];
  }

  // ═══ 304+ AC — Dragon Queen returns, North rebels, Long Night ═══
  return [
    { id: 'stark-reclaimed', label: 'HOUSE STARK', color: '148,163,184',
      path: 'M26,8 L46,8 L46,36 L26,36 Z',
      textX: 36, textY: 22, fontSize: 1.4 },
    { id: 'cersei-crown', label: 'QUEEN CERSEI', color: '220,38,38',
      path: 'M30,42 L48,42 L48,62 L30,62 Z',
      textX: 39, textY: 52, fontSize: 1.4 },
    { id: 'targaryen-return', label: 'DAENERYS TARGARYEN', color: '124,45,18',
      path: 'M42,48 L48,48 L48,54 L42,54 Z',
      textX: 45, textY: 58, fontSize: 1.0, dashed: true },
    { id: 'tyrell-reach', label: 'THE REACH', color: '22,163,74',
      path: 'M26,56 L36,56 L36,66 L26,66 Z',
      textX: 31, textY: 61, fontSize: 1.2, dashed: true },
    { id: 'martell', label: 'HOUSE MARTELL', color: '234,88,12',
      path: 'M30,66 L46,66 L46,72 L30,72 Z',
      textX: 38, textY: 70, fontSize: 1.2 },
    essos, dothraki, slavers_bay,
  ];
}

export default function WesterosMap({ characterLocation, onLocationClick, worldCharacters = [], events = [], timelinePosition = '' }) {
  const containerRef = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [pulsePhase, setPulsePhase] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setPulsePhase(p => (p + 1) % 100), 50);
    return () => clearInterval(interval);
  }, []);

  // Center on character location
  useEffect(() => {
    if (characterLocation && imageLoaded && containerRef.current) {
      const loc = LOCATIONS.find(l =>
        l.id === characterLocation || l.name.toLowerCase() === characterLocation?.toLowerCase()
      );
      if (loc) {
        const rect = containerRef.current.getBoundingClientRect();
        const targetZoom = 2;
        setZoom(targetZoom);
        setOffset({
          x: -(loc.x / 100 * rect.width * targetZoom - rect.width / 2),
          y: -(loc.y / 100 * rect.height * targetZoom - rect.height / 2),
        });
      }
    }
  }, [characterLocation, imageLoaded]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.5, Math.min(6, zoom * delta));
    const scale = newZoom / zoom;
    setOffset(prev => ({
      x: mouseX - scale * (mouseX - prev.x),
      y: mouseY - scale * (mouseY - prev.y),
    }));
    setZoom(newZoom);
  }, [zoom]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const handleMouseDown = (e) => {
    if (e.target.closest('.location-dot')) return;
    setDragging(true);
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };
  const handleMouseMove = (e) => {
    if (!dragging || !dragStart) return;
    setOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => { setDragging(false); setDragStart(null); };

  const charLoc = characterLocation ? LOCATIONS.find(l =>
    l.id === characterLocation || l.name.toLowerCase() === characterLocation?.toLowerCase()
  ) : null;

  const pulseScale = 1 + Math.sin(pulsePhase * 0.12) * 0.4;
  const pulseOpacity = 0.5 + Math.sin(pulsePhase * 0.08) * 0.3;

  // House-territory overlays removed — variable no longer needed
  // const houses = getHousesForYear(parseYear(timelinePosition));

  return (
    <div
      ref={containerRef}
      className="w-full h-full overflow-hidden relative"
      style={{ cursor: dragging ? 'grabbing' : 'grab', backgroundColor: '#1a0f08' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      data-testid="westeros-map"
    >
      <div
        className="absolute will-change-transform"
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
          width: '100%',
          height: '100%',
          transition: dragging ? 'none' : 'transform 0.1s ease-out',
        }}
      >
        {/* Parchment fallback background — always rendered, image lays on top if loaded */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, #3d2817 0%, #1a0f08 80%)',
            backgroundImage: `
              radial-gradient(circle at 20% 30%, rgba(139,94,52,0.12) 0%, transparent 50%),
              radial-gradient(circle at 70% 60%, rgba(92,63,35,0.12) 0%, transparent 50%)
            `,
          }}
        />

        {!imageFailed && (
          <img
            src={WESTEROS_IMAGE}
            alt="Westeros"
            className="w-full h-full object-cover select-none pointer-events-none"
            draggable={false}
            onLoad={() => setImageLoaded(true)}
            onError={() => { setImageFailed(true); setImageLoaded(true); }}
            style={{
              opacity: imageLoaded && !imageFailed ? 0.55 : 0,
              transition: 'opacity 0.5s',
              filter: 'sepia(0.55) brightness(0.65) contrast(1.1) saturate(0.85)',
            }}
          />
        )}

        {/* Dark tint overlay — ensures UI + territory labels stay legible over the map */}
        {imageLoaded && !imageFailed && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse at center, rgba(26,15,8,0.4) 0%, rgba(10,5,2,0.75) 100%)',
              mixBlendMode: 'multiply',
            }}
          />
        )}

        {/* House territory overlays removed — the base map already conveys the geography.
            Only location pins are drawn on top now. */}

        {/* Location markers */}
        {imageLoaded && LOCATIONS.map(loc => {
          const isHovered = hovered === loc.id;
          const isCharHere = charLoc?.id === loc.id;
          const dotSize = loc.major ? 6 : 3;
          const regionColor = {
            'The North': '#cbd5e1',
            'The Wall': '#e0f2fe',
            'The Riverlands': '#60a5fa',
            'The Vale': '#93c5fd',
            'The Westerlands': '#f87171',
            'Iron Islands': '#94a3b8',
            'The Crownlands': '#fbbf24',
            'The Stormlands': '#fde047',
            'The Reach': '#86efac',
            'Dorne': '#fb923c',
            'Essos': '#c084fc',
            "Slaver's Bay": '#d97706',
          }[loc.region] || '#d1d5db';

          return (
            <div
              key={loc.id}
              className="location-dot absolute"
              style={{
                left: `${loc.x}%`,
                top: `${loc.y}%`,
                transform: 'translate(-50%, -50%)',
                zIndex: isHovered || isCharHere ? 20 : 10,
              }}
              onMouseEnter={() => setHovered(loc.id)}
              onMouseLeave={() => setHovered(null)}
              onClick={(e) => { e.stopPropagation(); onLocationClick?.(loc); }}
              data-testid={`location-${loc.id}`}
            >
              {/* Character beacon — pulsing rings */}
              {isCharHere && (
                <>
                  <div className="absolute rounded-full" style={{
                    left: '50%', top: '50%',
                    width: `${20 * pulseScale}px`, height: `${20 * pulseScale}px`,
                    transform: 'translate(-50%, -50%)',
                    border: '1px solid #fbbf24',
                    opacity: pulseOpacity * 0.55,
                  }} />
                  <div className="absolute rounded-full" style={{
                    left: '50%', top: '50%',
                    width: `${32 * pulseScale}px`, height: `${32 * pulseScale}px`,
                    transform: 'translate(-50%, -50%)',
                    border: '1px solid #fbbf24',
                    opacity: pulseOpacity * 0.3,
                  }} />
                  <div className="absolute rounded-full" style={{
                    left: '50%', top: '50%',
                    width: '14px', height: '14px',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(251,191,36,0.3)',
                  }} />
                </>
              )}

              <div
                className="rounded-full cursor-pointer transition-all duration-150"
                style={{
                  width: `${isHovered ? dotSize + 4 : dotSize}px`,
                  height: `${isHovered ? dotSize + 4 : dotSize}px`,
                  backgroundColor: isCharHere ? '#fbbf24' : regionColor,
                  boxShadow: `0 0 ${isHovered ? 12 : loc.major ? 8 : 4}px ${regionColor}, 0 0 ${isHovered ? 20 : loc.major ? 12 : 0}px ${regionColor}`,
                  border: loc.major ? '1px solid rgba(255,255,255,0.35)' : 'none',
                }}
              />

              {(isHovered || (loc.major && zoom >= 1.2) || isCharHere) && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap pointer-events-none"
                  style={{ bottom: `${(isHovered ? dotSize + 8 : dotSize + 4)}px` }}
                >
                  <span
                    className="px-1.5 py-0.5 rounded"
                    style={{
                      fontFamily: 'serif',
                      fontSize: isHovered ? '11px' : '9px',
                      color: isCharHere ? '#fde047' : isHovered ? '#ffffff' : '#fef3c7',
                      backgroundColor: isHovered ? 'rgba(0,0,0,0.88)' : 'rgba(0,0,0,0.6)',
                      fontWeight: isCharHere ? 'bold' : 600,
                      textShadow: '0 0 4px rgba(0,0,0,0.95), 0 1px 2px rgba(0,0,0,0.9)',
                      letterSpacing: '0.03em',
                    }}
                  >
                    {loc.name}
                  </span>
                </div>
              )}

              {isHovered && (
                <div
                  className="absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-black/90 border border-amber-700/30 rounded px-2 py-1 whitespace-nowrap pointer-events-none z-50"
                  style={{ minWidth: '80px' }}
                >
                  <div className="text-[10px] font-serif italic text-amber-200/70">{loc.region}</div>
                  <div className="text-xs font-serif font-bold text-white">{loc.name}</div>
                  {isCharHere && <div className="text-[10px] font-serif text-amber-400 mt-0.5">You are here</div>}
                </div>
              )}
            </div>
          );
        })}

        {/* Event markers — ravens swooping in */}
        {imageLoaded && events.map((evt, i) => {
          const loc = LOCATIONS.find(l =>
            l.id === evt.system_id || l.name?.toLowerCase() === evt.system_id?.toLowerCase()
          );
          if (!loc) return null;
          return (
            <div
              key={`evt-${i}`}
              className="absolute pointer-events-none"
              style={{ left: `${loc.x}%`, top: `${loc.y}%`, transform: 'translate(-50%, -50%)', zIndex: 5 }}
            >
              <div
                className="rounded-full border-2 border-dashed border-red-400"
                style={{
                  width: '22px', height: '22px',
                  opacity: 0.6 + Math.sin(pulsePhase * 0.1 + i) * 0.3,
                  boxShadow: '0 0 8px rgba(248,113,113,0.6)',
                }}
              />
              <div
                className="absolute left-1/2 -translate-x-1/2 top-full mt-1 text-[9px] font-serif font-bold text-red-200 whitespace-nowrap px-1 rounded"
                style={{ backgroundColor: 'rgba(0,0,0,0.75)', textShadow: '0 0 4px rgba(0,0,0,0.95)' }}
              >
                {evt.label}
              </div>
            </div>
          );
        })}

        {/* Other characters in the world */}
        {imageLoaded && worldCharacters.filter(c => c.location).map(char => {
          const loc = LOCATIONS.find(l =>
            l.id === char.location || l.name?.toLowerCase() === char.location?.toLowerCase()
          );
          if (!loc) return null;
          return (
            <div
              key={char.character_id}
              className="absolute pointer-events-none"
              style={{ left: `${loc.x}%`, top: `${loc.y}%`, transform: 'translate(-50%, -50%)', zIndex: 15 }}
            >
              <div
                className="w-3.5 h-3.5 rounded-full border-2 border-orange-300"
                style={{ backgroundColor: 'rgba(251,146,60,0.4)', boxShadow: '0 0 6px rgba(251,146,60,0.8)' }}
              />
              <div
                className="absolute left-1/2 -translate-x-1/2 top-full mt-1 text-[9px] font-serif font-bold text-orange-200 whitespace-nowrap px-1 rounded"
                style={{ backgroundColor: 'rgba(0,0,0,0.7)', textShadow: '0 0 4px rgba(0,0,0,0.95)' }}
              >
                {char.name}
              </div>
            </div>
          );
        })}
      </div>

      {/* Zoom indicator */}
      <div className="absolute bottom-3 right-3 text-[10px] font-serif italic text-amber-700/70 bg-black/50 px-2 py-1 rounded pointer-events-none">
        {(zoom * 100).toFixed(0)}%
      </div>
    </div>
  );
}
